import { useRef } from "react";
import type { EmojiText } from "./useEmojiText";

/** A field the picker can insert into. */
export interface EmojiTarget {
  insert(text: string): boolean;
  /** False once the field has left the page (a removed paragraph or caption). */
  alive(): boolean;
  focus(): void;
}

/** A target that always reaches the field's latest helper (the helper object is new on every render). */
export function targetOf(get: () => { insert: EmojiText["insert"]; ref: { readonly current: Element | null } }): EmojiTarget {
  return {
    insert: (text) => get().insert(text),
    alive: () => !!get().ref.current?.isConnected,
    focus: () => (get().ref.current as HTMLElement | null)?.focus(),
  };
}

/**
 * One picker button for a form with several text fields: it inserts into the field that last had focus,
 * else into `fallback` (the form's main field, or the paragraph the button sits in).
 */
export function useLastFocusedEmoji() {
  const last = useRef<EmojiTarget | null>(null);
  const pick = (fallback: EmojiTarget | null) => (last.current?.alive() ? last.current : fallback);
  return {
    /** Give this to each field's focus handler. */
    onFocus: (target: EmojiTarget) => {
      last.current = target;
    },
    insert: (text: string, fallback: EmojiTarget | null): boolean => (pick(fallback)?.insert(text) ?? false),
    /** Puts focus back where `insert` would write. */
    focus: (fallback: EmojiTarget | null) => pick(fallback)?.focus(),
  };
}
